import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import Button from "theme/components/atoms/Button";
import LabelledIcon from "theme/components/molecules/LabelledIcon";
import useListenClickOutside from "theme/components/helpers/useListenClickOutside";
import useFocusTrap from "theme/components/helpers/a11y/useFocusTrap";
import { defineMessages, useIntl } from "react-intl";
import useKeyDownEvent from "theme/components/helpers/a11y/useKeyDownEvent";

const messages = defineMessages({
  close: {
    id: "components.molecules.Selector.close",
    defaultMessage: "Close",
  },
});

const SelectorOptions = ({
  isOpened,
  onClose,
  options,
  value,
  onSelect,
  spanWrapper = false,
}) => {
  const intl = useIntl();
  const containerRef = useRef();
  useFocusTrap(containerRef, 0, isOpened);

  useKeyDownEvent(
    containerRef,
    useCallback(
      (event) => {
        if (event.keyCode === 27) {
          onClose(event);
        }
      },
      [onClose]
    )
  );

  const Component = spanWrapper ? "span" : "div";

  return (
    <Component tabIndex={-1} className="selector__list" ref={containerRef}>
      <button
        className="sr-only sr-focusable"
        onClick={(event) => onClose(event)}
      >
        {intl.formatMessage(messages.close)}
      </button>
      {options.map((option) => (
        <Component className="selector__item" key={option.value}>
          <Button
            appearance="link"
            state={option.value === value ? "disabled" : undefined}
            onClick={() => {
              onSelect(option.value);
            }}
            onDisableClick={() => {}}
          >
            {option.label}
          </Button>
        </Component>
      ))}
    </Component>
  );
};

const Selector = ({
  options,
  current,
  value,
  onSelect,
  title,
  spanWrapper = false,
}) => {
  const [opened, setOpen] = useState(false);
  const [containerRef] = useListenClickOutside(() => setOpen(false));

  if (options.length < 2) {
    return null;
  }

  const Component = spanWrapper ? "span" : "div";

  return (
    <Component
      className={classNames("selector", {
        "selector--opened": opened,
      })}
      ref={containerRef}
    >
      <Button
        appearance="link"
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <LabelledIcon
          inverted={true}
          icon="chevron-down"
          title={title}
          size="mini"
        >
          {current}
        </LabelledIcon>
      </Button>
      <SelectorOptions
        options={options}
        value={value}
        onSelect={onSelect}
        isOpened={opened}
        onClose={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }}
        spanWrapper={spanWrapper}
      />
    </Component>
  );
};

export default Selector;
